import type { TodayCalendar, TodayCalendarItem } from '@fos/shared/contracts/today';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatDateShort, formatDayLong, formatDuration, formatMoneyTotal, plural } from '../../../lib/format';
import { useAccess } from '../../../lib/useAccess';
import { Button, IconButton } from '../../../ui/Button';
import { ListRow } from '../../../ui/ListRow';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Dot, type Tone } from '../../../ui/Status';
import { Tooltip } from '../../../ui/Tooltip';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import {
  type CalendarCounts,
  countItems,
  type DateRange,
  dayOfMonth,
  daysOf,
  dayTone,
  formatHours,
  itemsByDay,
  kindLabel,
  monthCells,
  monthLabel,
  shortTime,
  summaryLine,
  totalSeconds,
  WEEKDAY_LETTERS,
  WEEKDAY_SHORT,
  weekdayIndex,
  weekLabel,
} from '../calendar';
import type { TodaySummary } from '../today-data';

/**
 * Dziś → Tydzień („jak rozłożona praca”) i Miesiąc („z góry”), DECISIONS 2026-10-09 „Dziś: Dzień / Tydzień /
 * Miesiąc”. Czysto i mało naraz: bez kafelków KPI i długich list; szczegóły dnia dopiero po kliknięciu (DayPanel).
 * Kolor tylko z znaczeniem: spotkanie — niebieski, odnowienie / płatność — bursztyn, po terminie — czerwony,
 * zadanie i termin projektu — szary.
 */

const MAX_TILES = 3;

const TILE_BORDER: Record<Tone, string> = {
  accent: 'border-l-accent',
  warning: 'border-l-warning',
  danger: 'border-l-danger',
  neutral: 'border-l-line-strong',
  success: 'border-l-success',
};

/** Tło dnia w siatce miesiąca (kolor najważniejszej rzeczy). */
const CELL_TONE: Record<Tone, string> = {
  danger: 'bg-danger/15 text-danger font-medium',
  warning: 'bg-warning/15 text-warning font-medium',
  accent: 'bg-accent/12 text-accent font-medium',
  neutral: 'bg-elevated text-fg',
  success: 'bg-success/15 text-success',
};

function itemTitle(item: TodayCalendarItem): string {
  return item.time ? `${shortTime(item.time)} ${item.title}` : item.title;
}

/** „9 października: 1 spotkanie, 2 zadania, 1 po terminie” (czytnik ekranu, dymek dnia). */
function countsText(c: CalendarCounts): string {
  const parts: string[] = [];
  if (c.overdue) parts.push(`${c.overdue} po terminie`);
  if (c.meetings) parts.push(`${c.meetings} ${plural(c.meetings, ['spotkanie', 'spotkania', 'spotkań'])}`);
  if (c.renewals) parts.push(`${c.renewals} ${plural(c.renewals, ['odnowienie', 'odnowienia', 'odnowień'])}`);
  if (c.tasks) parts.push(`${c.tasks} ${plural(c.tasks, ['zadanie', 'zadania', 'zadań'])}`);
  if (c.projectDeadlines) {
    parts.push(
      `${c.projectDeadlines} ${plural(c.projectDeadlines, ['termin projektu', 'terminy projektów', 'terminów projektów'])}`,
    );
  }
  return parts.length > 0 ? parts.join(', ') : 'nic zaplanowanego';
}

// ── Nawigacja zakresu ───────────────────────────────────────────────────────

export function RangeNav({
  unit,
  onPrev,
  onNext,
  onToday,
}: {
  unit: 'tydzień' | 'miesiąc';
  onPrev: () => void;
  onNext: () => void;
  /** brak = zakres zawiera dziś (przycisk „Dziś” ukryty) */
  onToday?: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      {onToday && (
        <Button variant="ghost" size="s" onClick={onToday} className={`max-md:h-11 ${TOUCH_BUTTON}`}>
          Dziś
        </Button>
      )}
      <IconButton
        icon={ChevronLeft}
        size={32}
        label={`Poprzedni ${unit}`}
        onClick={onPrev}
        className="max-md:size-11"
      />
      <IconButton
        icon={ChevronRight}
        size={32}
        label={`Następny ${unit}`}
        onClick={onNext}
        className="max-md:size-11"
      />
    </div>
  );
}

// ── Kafelek ─────────────────────────────────────────────────────────────────

function CalendarTile({ item }: { item: TodayCalendarItem }) {
  return (
    <Link
      to={item.to}
      title={`${kindLabel(item)}: ${itemTitle(item)}${item.context ? ` · ${item.context}` : ''}`}
      data-kind={item.kind}
      data-tone={item.tone}
      className={cn(
        'flex min-w-0 flex-col rounded-md border-l-2 bg-surface py-1 pr-1.5 pl-2 text-caption',
        'transition-colors hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent',
        TILE_BORDER[item.tone],
      )}
    >
      {/* godzina spotkania w pierwszej linii, tytuł (najwyżej 2 linie) na całą szerokość kafelka */}
      {item.time && <span className="text-fg-secondary tabular-nums">{shortTime(item.time)}</span>}
      <span className="line-clamp-2 min-w-0 break-words text-fg">{item.title}</span>
    </Link>
  );
}

// ── Lista dnia ──────────────────────────────────────────────────────────────

/** Lista dnia po kliknięciu dnia (tydzień, miesiąc): wszystkie pozycje i wpisany czas. */
export function DayPanel({
  date,
  items,
  seconds,
  today,
  onClose,
}: {
  date: string;
  items: TodayCalendarItem[];
  seconds: number | null;
  today: string;
  onClose: () => void;
}) {
  const now = new Date();
  return (
    <Section
      title={
        <>
          {formatDayLong(date)}
          {date === today ? ' · dziś' : ''}
        </>
      }
      action={<IconButton icon={X} size={32} label="Zamknij dzień" onClick={onClose} />}
    >
      {items.length === 0 ? (
        <EmptyState compact title="Nic zaplanowanego na ten dzień." />
      ) : (
        items.map((item) => (
          <ListRow
            key={item.id}
            to={item.to}
            leading={<Dot tone={item.tone} />}
            title={<span className="font-normal">{item.title}</span>}
            meta={
              item.time
                ? `${shortTime(item.time)}${item.endTime ? `–${shortTime(item.endTime)}` : ''}`
                : item.overdue && item.dueDate
                  ? `po terminie · ${formatDateShort(item.dueDate, now)}`
                  : kindLabel(item).toLowerCase()
            }
            trailing={
              item.context ? (
                <span title={item.context} className="hidden min-w-0 truncate text-body-s text-fg-muted md:block">
                  {item.context}
                </span>
              ) : undefined
            }
          />
        ))
      )}
      {seconds !== null && seconds > 0 && (
        <p className="pt-3 text-body-s text-fg-secondary">Wpisany czas: {formatDuration(seconds)}</p>
      )}
    </Section>
  );
}

function secondsOn(data: TodayCalendar | undefined, date: string): number | null {
  if (!data?.days) return null;
  return data.days.find((d) => d.date === date)?.seconds ?? 0;
}

// ── Tydzień ─────────────────────────────────────────────────────────────────

export function WeekView({
  range,
  data,
  pending,
  failed,
  onRetry,
  retrying,
  today,
  selected,
  onSelect,
  nav,
}: {
  range: DateRange;
  data: TodayCalendar | undefined;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  retrying: boolean;
  today: string;
  selected: string | null;
  onSelect: (date: string) => void;
  nav: ReactNode;
}) {
  const days = daysOf(range);
  const byDay = itemsByDay(data?.items ?? []);
  const scroller = useRef<HTMLDivElement>(null);

  // telefon: tydzień przewijany poziomo — dziś na początku widoku (bez przewijania strony); tylko po zmianie tygodnia
  // albo wczytaniu danych, żeby odświeżenie nie cofało przewiniętej listy
  const weekKey = `${range.from}|${today}|${pending ? 'p' : 'd'}`;
  useEffect(() => {
    if (!weekKey) return;
    const el = scroller.current;
    const target = el?.querySelector<HTMLElement>('[data-today="true"]');
    if (el && target && el.scrollWidth > el.clientWidth) el.scrollLeft = Math.max(0, target.offsetLeft - 16);
  }, [weekKey]);

  return (
    <Section aria-labelledby="week-title">
      <div className="flex items-center justify-between gap-3">
        <h2 id="week-title" className="min-w-0 text-body-m font-medium text-fg">
          {weekLabel(range)}
        </h2>
        {nav}
      </div>
      {failed ? (
        <ErrorState onRetry={onRetry} retrying={retrying} />
      ) : (
        <>
          <p data-testid="week-summary" className="mt-1 min-h-5 text-body-s text-fg-secondary">
            {pending || !data ? '' : summaryLine(countItems(data.items), totalSeconds(data.days))}
          </p>
          <div
            ref={scroller}
            className="-mx-4 mt-4 snap-x overflow-x-auto px-4 pb-1 md:mx-0 md:overflow-visible md:px-0"
          >
            <ol
              aria-label="Dni tygodnia"
              className="grid auto-cols-[minmax(136px,44%)] grid-flow-col gap-2 md:grid-flow-row md:grid-cols-7"
            >
              {days.map((date) => {
                const items = byDay.get(date) ?? [];
                const shown = items.slice(0, MAX_TILES);
                const more = items.length - shown.length;
                const seconds = secondsOn(data, date);
                const isToday = date === today;
                return (
                  <li
                    key={date}
                    data-date={date}
                    data-today={isToday ? 'true' : undefined}
                    className={cn(
                      'flex min-h-36 min-w-0 snap-start flex-col rounded-lg border p-2',
                      isToday ? 'border-accent' : selected === date ? 'border-fg-muted' : 'border-line',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(date)}
                      aria-pressed={selected === date}
                      aria-label={`${formatDayLong(date)}${isToday ? ' (dziś)' : ''}: ${countsText(countItems(items))}`}
                      className="flex items-baseline gap-1.5 rounded-sm text-left hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:-mx-2 max-md:-mt-2 max-md:min-h-11 max-md:items-center max-md:px-2"
                    >
                      <span className="text-caption text-fg-muted">{WEEKDAY_SHORT[weekdayIndex(date)]}</span>
                      <span className={cn('text-body-s font-medium', isToday ? 'text-accent' : 'text-fg')}>
                        {dayOfMonth(date)}
                      </span>
                    </button>
                    {pending ? (
                      <Skeleton className="mt-2 h-6 rounded-md" />
                    ) : (
                      <ul className="mt-2 flex min-w-0 flex-col gap-1">
                        {shown.map((item) => (
                          <li key={item.id} className="min-w-0">
                            <CalendarTile item={item} />
                          </li>
                        ))}
                      </ul>
                    )}
                    {more > 0 && (
                      <button
                        type="button"
                        onClick={() => onSelect(date)}
                        className={cn(
                          'mt-1 self-start rounded-sm px-1 text-caption text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent',
                          TOUCH_TEXT,
                        )}
                      >
                        +{more} więcej
                      </button>
                    )}
                    {seconds !== null && seconds > 0 && (
                      <span className="mt-auto pt-2 text-caption text-fg-muted tabular-nums" title="Wpisany czas">
                        {formatHours(seconds)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}
    </Section>
  );
}

// ── Miesiąc ─────────────────────────────────────────────────────────────────

function MonthNumber({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-h3 text-fg tabular-nums">{value}</span>
      <span className="text-caption text-fg-muted">{label}</span>
    </div>
  );
}

function DayHint({ date, items }: { date: string; items: TodayCalendarItem[] }) {
  const shown = items.slice(0, MAX_TILES);
  return (
    <div className="flex flex-col gap-0.5 py-0.5">
      <span className="font-medium">{formatDayLong(date)}</span>
      {shown.map((i) => (
        <span key={i.id} className="truncate">
          {itemTitle(i)}
        </span>
      ))}
      {items.length > shown.length && <span>+{items.length - shown.length} więcej</span>}
    </div>
  );
}

export function MonthView({
  range,
  data,
  pending,
  failed,
  onRetry,
  retrying,
  today,
  selected,
  onSelect,
  nav,
}: {
  range: DateRange;
  data: TodayCalendar | undefined;
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
  retrying: boolean;
  today: string;
  selected: string | null;
  onSelect: (date: string) => void;
  nav: ReactNode;
}) {
  const access = useAccess();
  const byDay = itemsByDay(data?.items ?? []);
  const items = data?.items ?? [];
  const seconds = totalSeconds(data?.days ?? null);
  const unbilled = data?.unbilled;
  const numbers: { label: string; value: string }[] = [];
  if (data) {
    if (seconds !== null) numbers.push({ label: 'godziny w miesiącu', value: formatHours(seconds) });
    if (access.flag('seesRates') && unbilled?.amount !== undefined) {
      numbers.push({
        label: 'do zafakturowania',
        value: formatMoneyTotal(
          { amount: unbilled.amount, currency: unbilled.currency ?? 'PLN', otherAmounts: unbilled.otherAmounts },
          { short: true },
        ),
      });
    }
    if (access.can('maintenance')) {
      numbers.push({ label: 'odnowienia', value: String(items.filter((i) => i.kind === 'renewal').length) });
    }
    if (access.can('projects')) {
      numbers.push({
        label: 'terminy projektów',
        value: String(items.filter((i) => i.kind === 'project_deadline').length),
      });
    }
  }

  return (
    <Section aria-labelledby="month-title">
      <div className="flex items-center justify-between gap-3">
        <h2 id="month-title" className="min-w-0 text-body-m font-medium text-fg">
          {monthLabel(range)}
        </h2>
        {nav}
      </div>
      {failed ? (
        <ErrorState onRetry={onRetry} retrying={retrying} />
      ) : (
        <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-start md:gap-12">
          <div className="w-full max-w-[340px] shrink-0">
            <div aria-hidden className="grid grid-cols-7 gap-1 pb-1">
              {WEEKDAY_LETTERS.map((l, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: stałe litery dni (dwa „P”)
                <span key={i} className="text-center text-caption text-fg-muted">
                  {l}
                </span>
              ))}
            </div>
            <ol aria-label={`Dni: ${monthLabel(range)}`} className="grid grid-cols-7 gap-1">
              {monthCells(range).map((date, i) => {
                if (!date) {
                  // biome-ignore lint/suspicious/noArrayIndexKey: puste pola przed 1. dniem
                  return <li key={`blank-${i}`} aria-hidden />;
                }
                const dayItems = byDay.get(date) ?? [];
                const tone = pending ? null : dayTone(dayItems);
                const isToday = date === today;
                const cell = (
                  <button
                    type="button"
                    data-date={date}
                    data-tone={tone ?? undefined}
                    aria-pressed={selected === date}
                    aria-label={`${formatDayLong(date)}${isToday ? ' (dziś)' : ''}: ${countsText(countItems(dayItems))}`}
                    onClick={() => onSelect(date)}
                    className={cn(
                      'flex aspect-square w-full min-w-0 items-center justify-center rounded-md text-body-s tabular-nums',
                      'transition-shadow hover:ring-1 hover:ring-line-strong focus-visible:outline-2 focus-visible:outline-accent',
                      tone ? CELL_TONE[tone] : 'text-fg-secondary',
                      isToday && 'ring-2 ring-accent',
                      selected === date && !isToday && 'ring-2 ring-fg-muted',
                    )}
                  >
                    {dayOfMonth(date)}
                  </button>
                );
                return (
                  <li key={date} className="min-w-0">
                    {dayItems.length > 0 ? (
                      <Tooltip content={<DayHint date={date} items={dayItems} />}>{cell}</Tooltip>
                    ) : (
                      cell
                    )}
                  </li>
                );
              })}
            </ol>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-caption text-fg-muted" aria-label="Kolory dni">
              <li className="inline-flex items-center gap-1.5">
                <Dot tone="danger" /> po terminie
              </li>
              <li className="inline-flex items-center gap-1.5">
                <Dot tone="warning" /> odnowienie
              </li>
              <li className="inline-flex items-center gap-1.5">
                <Dot tone="accent" /> spotkanie
              </li>
              <li className="inline-flex items-center gap-1.5">
                <Dot tone="neutral" /> zadanie
              </li>
            </ul>
          </div>
          {pending ? (
            <Skeleton className="h-24 w-full max-w-sm rounded-lg" />
          ) : (
            numbers.length > 0 && (
              <div className="grid grid-cols-2 gap-x-8 gap-y-5 md:min-w-[260px]">
                {numbers.map((n) => (
                  <MonthNumber key={n.label} label={n.label} value={n.value} />
                ))}
              </div>
            )
          )}
        </div>
      )}
    </Section>
  );
}

// ── Wymaga Ciebie (pasek) ───────────────────────────────────────────────────

/** Liczby „Wymaga Ciebie” (Tydzień): jeden spokojny pasek zamiast listy. */
export function needsYouCounts(data: TodaySummary): string[] {
  const n = (kind: string) => data.needsYou.filter((i) => i.kind === kind).length;
  const parts: string[] = [];
  const alarms = data.alarms.open;
  if (alarms) parts.push(`${alarms} ${plural(alarms, ['alarm', 'alarmy', 'alarmów'])}`);
  const approvals = data.approvals.pending;
  if (approvals) parts.push(`${approvals} ${plural(approvals, ['akceptacja', 'akceptacje', 'akceptacji'])}`);
  const ai = n('agent_run');
  if (ai) parts.push(`${ai} ${plural(ai, ['wynik AI', 'wyniki AI', 'wyników AI'])}`);
  const messages = n('message');
  if (messages) parts.push(`${messages} ${plural(messages, ['wiadomość', 'wiadomości', 'wiadomości'])} do przypisania`);
  const blocked = n('task');
  if (blocked) parts.push(`${blocked} ${plural(blocked, ['zablokowane', 'zablokowane', 'zablokowanych'])}`);
  const renewals = n('renewal');
  if (renewals) parts.push(`${renewals} ${plural(renewals, ['odnowienie', 'odnowienia', 'odnowień'])}`);
  return parts;
}

export function NeedsYouBar({ data, onShow }: { data: TodaySummary; onShow: () => void }) {
  const parts = needsYouCounts(data);
  const tone: Tone | null = data.alarms.open > 0 ? 'danger' : parts.length > 0 ? 'accent' : null;
  return (
    <section
      aria-label="Wymaga Ciebie"
      className="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-line bg-panel px-4 py-2.5 md:flex-nowrap md:px-8"
    >
      {tone && <Dot tone={tone} />}
      <span className="text-body-s font-medium text-fg">Wymaga Ciebie</span>
      {/* telefon: liczby w drugiej linii na całą szerokość; desktop: jedna linia */}
      <span className="order-last min-w-0 basis-full text-body-s text-fg-secondary md:order-none md:flex-1 md:basis-auto">
        {parts.length > 0 ? parts.join(' · ') : 'nic nie czeka na Twoją decyzję'}
      </span>
      {parts.length > 0 && (
        <Button
          variant="ghost"
          size="s"
          iconRight={ChevronRight}
          onClick={onShow}
          className={`ml-auto md:ml-0 ${TOUCH_BUTTON}`}
        >
          Zobacz
        </Button>
      )}
    </section>
  );
}
